import {useEffect, useState} from "react";
import {InventoryService} from "../product/Service";
import {CustomerService} from "../customer/Service";
import Select from "react-select";
import {Link} from "react-router-dom";
import {PurchaseService} from "./Service";
import {UploadService} from "../../utils/UploadService";
import {toast} from "react-hot-toast";
import {VariantService} from "../variant/Service";
import {SettingService} from "../setting/Service";

const AddPurchase = () => {
    // Services
    const purchaseService = new PurchaseService();
    const inventoryService = new InventoryService();
    const variantService = new VariantService();
    const customerService = new CustomerService();
    const uploadService = new UploadService();
    const settingService = new SettingService();

    const [purchaseNumber, setPurchaseNumber] = useState('');
    const [vendor, setVendor] = useState<{ label: string, value: string } | null>(null);
    const [date, setDate] = useState('');
    const [paymentMethod, setPaymentMethod] = useState<{ label: string, value: string } | null>(null);
    const [amountPaid, setAmountPaid] = useState(0);
    const [modeOfPayment, setModeOfPayment] = useState('');
    const [comments, setComments] = useState('');
    const [transporter, setTransporter] = useState<{ label: string, value: string } | null>(null);
    const [bill, setBill] = useState<File|null>(null);
    const [discount, setDiscount] = useState(0);
    const [total, setTotal] = useState(0);
    const [products, setProducts] = useState<any[]>([]);
    const [taxableAmount, setTaxableAmount] = useState(0);
    const [totalDiscount, setTotalDiscount] = useState(0);
    const [tax, setTax] = useState<{ label: string, value: string } | null>(null);
    const [roundOff, setRoundOff] = useState(false);

    // Options
    const [vendorOptions, setVendorOptions] = useState<{ label: string, value: string }[]>([]);
    const [paymentMethodOptions, setPaymentMethodOptions] = useState<{ label: string, value: string }[]>([]);
    const [transporterOptions, setTransporterOptions] = useState<{ label: string, value: string }[]>([]);
    const [productOptions, setProductOptions] = useState<{ label: string, value: string }[]>([]);
    const [allProducts, setAllProducts] = useState<any[]>([]);
    const [taxTypeOptions, setTaxTypeOptions] = useState<{ label: string, value: string }[]>([]);
    const [variantOptions, setVariantOptions] = useState<{ label: string, value: string, product: any, variant: any }[]>([]);

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        // Product should not be empty
        if (products.length === 0) {
            toast.error('Please add products');
            return;
        }
        toast.loading('Adding purchase...');
        // Upload bill
        const billUrl = bill ? await uploadImage(bill) : null;
        const data = {
            number: purchaseNumber,
            vendor: vendor?.value,
            date,
            purchaseMode: paymentMethod?.value,
            amountPaid,
            modeOfPayment,
            comments,
            transporter: transporter?.value,
            tax: tax?.value,
            bill: billUrl,
            discount,
            products: products.map((p: any) => ({
                product: p.id,
                quantity: p.quantity,
                salePrice: p.salePrice,
                purchasePrice: p.purchasePrice,
                variant: p.variant,
            })),
            roundOff,
        }
        const res = await purchaseService.addPurchase(data);
        toast.dismiss();
        if (res) {
            toast.success('Purchase added successfully');
            e.target.reset();
            setPurchaseNumber('');
            setVendor(null);
            setTax(null);
            setDate('');
            setPaymentMethod(null);
            setAmountPaid(0);
            setModeOfPayment('');
            setComments('');
            setTransporter(null);
            setBill(null);
            setDiscount(0);
            setTotal(0);
            setProducts([]);
            setTaxableAmount(0);
            setTotalDiscount(0);
            setRoundOff(false);
        }

    }

    const loadVariants = async () => {
        const variants = await variantService.all();
        setVariantOptions(variants.map((v: any) => ({label: `${v.size} (${v.unit}) / ${v.type}`, value: v.id, product: v.product, variant: v})));
    }

    const uploadImage = async (file: any) => {
        const res = await uploadService.upload(file);
        return res.length > 0 ? res[0] : null;
    }

    // Set options
    useEffect(() => {
        const fetchVendors = async () => {
            const vendors = await customerService.all();
            setVendorOptions(vendors.filter((c: any) => c.type === 'Vendor').map((v: any) => ({
                label: v.name,
                value: v.id
            })));
            setTransporterOptions(vendors.filter((c: any) => c.type === 'Transporter').map((v: any) => ({
                label: v.name,
                value: v.id
            })));
        }
        const fetchProducts = async () => {
            const products = await inventoryService.allProducts();
            setProductOptions(products.map((v: any) => ({label: v.name, value: v.id})));
            setAllProducts(products.map((v: any) => ({...v, quantity: 1})));
        }
        const purchaseModeOptions = [
            {label: 'Cash', value: 'Cash'},
            {label: 'Credit', value: 'Credit'},
        ];
        setPaymentMethodOptions(purchaseModeOptions);
        fetchVendors();
        fetchProducts();
        loadVariants();
    }, [])

    useEffect(() => {
        const getTaxTypes = async () => {
            const res = await settingService.allTaxTypes();
            if (res) {
                const options = res.map((item: any) => {
                    return {
                        label: `${item.name} (${item.percentage}%)`,
                        value: item.id,
                        percentage: item.percentage
                    }
                });
                setTaxTypeOptions(options);
            }
        }
        getTaxTypes();
    }, [])

    useEffect(() => {
        // Calculate taxable amount
        const taxableAmount = products.reduce((acc: number, product: any) => {
            return acc + (product.quantity * product.purchasePrice);
        }, 0);
        setTaxableAmount(taxableAmount);

        // Calculate discount
        const totalDiscount = discount > 0 ? (taxableAmount * discount) / 100 : 0;
        setTotalDiscount(totalDiscount);

        // Calculate tax
        let taxAmount = 0;
        if (tax) {
            //@ts-ignore
            taxAmount = (taxableAmount - totalDiscount) * tax.percentage / 100;
        }

        const total = taxableAmount + taxAmount - totalDiscount;
        setTotal(total);
    }, [products, roundOff, discount]);

    // Set purchase date to current date
    useEffect(() => {
        const date = new Date();
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        setDate(`${year}-${month}-${day}`);
    }, [])

    return (
        <>
            <div className="card mb-0">
                <div className="card-body">
                    <div className="content-page-header">
                        <h5>Add Purchases</h5>
                    </div>
                    <form onSubmit={handleSubmit} className="row">
                        <div className="col-md-12">
                            <div className="form-group-item border-0 mb-0">
                                <div className="row align-item-center">
                                    <div className="col-lg-4 col-md-6 col-sm-12">
                                        <div className="input-block mb-3">
                                            <label>Purchases Number <span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Enter Purchases Number"
                                                value={purchaseNumber}
                                                onChange={(e) => setPurchaseNumber(e.target.value)}
                                                required={true}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-lg-4 col-md-6 col-sm-12">
                                        <div className="input-block mb-3">
                                            <label>Select Vendor <span className="text-danger">*</span></label>
                                            <ul className="form-group-plus css-equal-heights">
                                                <li>
                                                    <Select
                                                        name="vendor"
                                                        options={vendorOptions}
                                                        value={vendor}
                                                        onChange={setVendor}
                                                        required={true}/>
                                                </li>
                                                <li>
                                                    <Link
                                                        className="btn btn-primary form-plus-btn"
                                                        to={'/vendors/add'}
                                                    >
                                                        <i className="fas fa-plus-circle"/>
                                                    </Link>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                    <div className="col-lg-4 col-md-6 col-sm-12">
                                        <div className="input-block mb-3">
                                            <label>Purchases Date <span className="text-danger">*</span></label>
                                            <input
                                                type="date"
                                                className="datetimepicker form-control"
                                                placeholder="Select Date"
                                                value={date}
                                                onChange={(e) => setDate(e.target.value)}
                                                required={true}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-lg-4 col-md-6 col-sm-12">
                                        <div className="input-block mb-3">
                                            <label>Purchase Mode </label>
                                            <Select
                                                name="purchase-mode"
                                                options={paymentMethodOptions}
                                                value={paymentMethod}
                                                onChange={setPaymentMethod}
                                                required={false}/>
                                        </div>
                                    </div>
                                    <div className="col-lg-4 col-12 mb-3">
                                        <label htmlFor="tax" className="form-label">Tax</label>
                                        <Select
                                            name="tax"
                                            options={taxTypeOptions}
                                            value={tax}
                                            menuPortalTarget={document.body}
                                            styles={{
                                                menuPortal: base => ({
                                                    ...base,
                                                    zIndex: 9999
                                                })
                                            }}
                                            onChange={setTax}/>
                                    </div>
                                    {paymentMethod?.value === 'Credit' && (<>
                                        <div className="col-lg-6 col-md-6 col-sm-12">
                                            <div className="input-block mb-3">
                                                <label>Amount Payed</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    placeholder="Enter total amount payed"
                                                    value={amountPaid}
                                                    onChange={(e) => setAmountPaid(parseInt(e.target.value))}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-lg-6 col-md-6 col-sm-12">
                                            <div className="input-block mb-3">
                                                <label>Mode of Payment</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Enter Mode of Payment"
                                                    value={modeOfPayment}
                                                    onChange={(e) => setModeOfPayment(e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </>)}
                                    <div className="col-lg-4 col-md-6 col-sm-12">
                                        <div className="input-block mb-3">
                                            <label>Comment</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Enter Mode of Payment"
                                                value={comments}
                                                onChange={(e) => setComments(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-lg-12">
                                        <div className="input-block mb-3">
                                            <label>Products <span className="text-danger">*</span></label>
                                            <ul className="form-group-plus css-equal-heights">
                                                <li>
                                                    <Select
                                                        name="product"
                                                        options={productOptions}
                                                        onChange={(e) => {
                                                            const product = allProducts.find((p: any) => p.id === e?.value);
                                                            if (product) {
                                                                setProducts([...products, product]);
                                                            }
                                                        }}
                                                    />
                                                </li>
                                                <li>
                                                    <Link
                                                        className="btn btn-primary form-plus-btn"
                                                        to={'/products/add'}
                                                    >
                                                        <i className="fas fa-plus-circle"/>
                                                    </Link>
                                                </li>
                                            </ul>
                                        </div>

                                    </div>
                                </div>
                            </div>
                            <div className="form-group-item">
                                <div className="card-table">
                                    <div className="card-body">
                                        <div className="table-responsive no-pagination">
                                            <table className="table table-center table-hover datatable">
                                                <thead className="thead-light">
                                                <tr>
                                                    <th>Product / Service</th>
                                                    <th>Variant</th>
                                                    <th>Quantity</th>
                                                    <th>Rate</th>
                                                    <th>Amount</th>
                                                    <th className="no-sort">Action</th>
                                                </tr>
                                                </thead>
                                                <tbody>
                                                {products.map((product: any, index) => (<tr key={product.id}>
                                                    <td>{product.name}</td>
                                                    <td>
                                                        <div className=" mb-3">
                                                            <label>Variant <span
                                                                className="text-danger">*</span></label>
                                                            <Select
                                                                name="variant"
                                                                menuPortalTarget={document.body}
                                                                styles={{
                                                                    menuPortal: base => ({
                                                                        ...base,
                                                                        zIndex: 9999
                                                                    })
                                                                }}
                                                                options={variantOptions.filter((v: any) => v.product['id'] === product.id)}
                                                                onChange={(e) => {
                                                                    const newProducts = products.map((p: any, i: number) => {
                                                                        if (i === index) {
                                                                            return {
                                                                                ...p,
                                                                                variant: parseInt(e?.value ?? '0'),
                                                                                purchasePrice: e?.variant?.purchasePrice ?? 0,
                                                                                salePrice: e?.variant?.salePrice ?? 0,
                                                                            };
                                                                        }
                                                                        return p;
                                                                    });
                                                                    setProducts(newProducts);
                                                                }}
                                                            />
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            placeholder="Enter quantity"
                                                            value={product.quantity}
                                                            onChange={(e) => {
                                                                const newProducts = products.map((p: any, i: number) => {
                                                                    if (i === index) {
                                                                        return {
                                                                            ...p,
                                                                            quantity: parseInt(e.target.value)
                                                                        };
                                                                    }
                                                                    return p;
                                                                });
                                                                setProducts(newProducts);
                                                            }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            placeholder="Enter sale price"
                                                            step='0.01'
                                                            value={product.purchasePrice}
                                                            onChange={(e) => {
                                                                const newProducts = products.map((p: any, i: number) => {
                                                                    if (i === index) {
                                                                        return {
                                                                            ...p,
                                                                            purchasePrice: parseFloat(e.target.value)
                                                                        };
                                                                    }
                                                                    return p;
                                                                });
                                                                setProducts(newProducts);
                                                            }}
                                                        />
                                                    </td>
                                                    <td>₹{product.purchasePrice * product.quantity}</td>
                                                    <td className="d-flex align-items-center h-100">
                                                        <button
                                                            className="btn btn-danger btn-sm mb-2"
                                                            onClick={() => {
                                                                const newProducts = products.filter((p: any, i: number) => i !== index);
                                                                setProducts(newProducts);
                                                            }}
                                                        >
                                                            Delete
                                                        </button>
                                                    </td>
                                                </tr>))}

                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                {/*<div className="col-lg-6">*/}
                                {/*    <div className="input-block mb-3">*/}
                                {/*        <label>Discount(%)</label>*/}
                                {/*        <input*/}
                                {/*            type="number"*/}
                                {/*            className="form-control"*/}
                                {/*            value={discount}*/}
                                {/*            onChange={(e) => setDiscount(parseInt(e.target.value))}*/}
                                {/*        />*/}
                                {/*    </div>*/}
                                {/*</div>*/}
                                <div className="col-lg-12">
                                    <div className="input-block mb-3">
                                        <label>Bill</label>
                                        <input
                                            type="file"
                                            className="form-control"
                                            onChange={(e) => {
                                                if (e.target.files) {
                                                    setBill(e.target.files[0]);
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="form-group-item border-0 p-0">
                                <div className="row">
                                    <div className="col-12">
                                        <div className="form-group-bank">
                                            <div className="invoice-total-box">
                                                <div className="invoice-total-inner">
                                                    <p>
                                                        Taxable Amount <span>₹{taxableAmount}</span>
                                                    </p>
                                                    <p>
                                                        Discount <span>₹{totalDiscount}</span>
                                                    </p>
                                                    <p>
                                                        Tax <span>₹{
                                                        //     @ts-ignore
                                                        tax ? (taxableAmount - totalDiscount) * tax.percentage / 100 : 0
                                                    }</span>
                                                    </p>
                                                    <div className="status-toggle justify-content-between">
                                                        <div className="d-flex align-center">
                                                            <p>Round Off </p>
                                                            <input
                                                                id="rating_1"
                                                                className="check"
                                                                type="checkbox"
                                                                defaultChecked={false}
                                                                onChange={(e) => {
                                                                    setRoundOff(e.target.checked);
                                                                }}
                                                            />
                                                            <label
                                                                htmlFor="rating_1"
                                                                className="checktoggle checkbox-bg"
                                                            >
                                                                checkbox
                                                            </label>
                                                        </div>
                                                        <span>₹{total}</span>
                                                    </div>
                                                </div>
                                                <div className="invoice-total-footer">
                                                    <h4>
                                                        Total Amount <span>₹{total}</span>
                                                    </h4>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="btn-path text-end">
                                <button type="reset" onClick={() => {
                                    window.history.back();
                                }} className="btn btn-cancel bg-primary-light me-2">
                                    Back
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    Save
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

        </>
    );
}

export default AddPurchase;